import { useContext, useEffect, useState } from 'react';
import Highcharts from 'highcharts';

import AccountContext from 'contexts/AccountContext';
import DashboardContextMenuContext from 'contexts/DashboardContextMenuContext';
import getGroupByField from 'hooks/useMetricMatrix/getGroupByField';
import useDateScope from 'hooks/useDateScope';
import useV5ChartData from 'hooks/useV5ChartData';
import useChartDrillDowns from 'hooks/useChartDrillDowns';
import useValueFormatters from 'hooks/useValueFormatters';
import bubble from '../highchartOptions/bubbleOptions';
import toScatterPoints from '../toScatterPoints';

interface BubblePoint {
  name: string;
  x: number;
  y: number;
  z: number;
}

// Bubble reuses the standard metric + dimension pipeline. series[0] = x,
// series[1] = y, series[2] = size (optional; uniform when absent). Each
// dimension member becomes one (x, y, z) point by joining the metrics'
// responses on the group field.
const useBubbleData = ({
  chartDefinition,
  chartRef,
}: {
  chartDefinition: V5ChartDefinition;
  chartRef: React.MutableRefObject<any>;
}): {
  options: Highcharts.Options | undefined;
  isNoData: boolean;
  isReady: boolean;
} => {
  const { unitsLocale } = useContext(AccountContext);
  const { openMenu } = useContext(DashboardContextMenuContext);

  // Derive (don't freeze in useState) — the gadget can mount before dimensionA
  // is set in the config modal, which would otherwise lock groupField to ''.
  const groupField = getGroupByField(chartDefinition);
  const [options, setOptions] = useState<Highcharts.Options | undefined>();
  const [isNoDataToDisplay, setIsNoDataToDisplay] = useState<boolean>(false);

  const { data, isLoading } = useV5ChartData(chartDefinition);
  const { formatMetric } = useValueFormatters();
  const dateScope = useDateScope({});
  const { onDrillDown, syncChart } = useChartDrillDowns(
    groupField,
    chartRef,
    'bubble',
    chartDefinition,
  );

  useEffect(() => {
    syncChart();
  }, [syncChart, onDrillDown, data]);

  useEffect(() => {
    if (!data) {
      return;
    }

    const joined = toScatterPoints({
      data,
      chartDef: chartDefinition,
      groupField,
    });
    // Bubble needs at least an x and y metric plus a dimension to plot per.
    if (!joined) {
      setIsNoDataToDisplay(true);
      setOptions(undefined);
      return;
    }
    const { x: xEntry, y: yEntry, size: sizeEntry, metricIds } = joined;
    const points: BubblePoint[] = joined.points;

    const baseBubbleOptions = bubble({
      title: ' ',
      openMenu,
      groupField,
      fieldType: chartDefinition.dimensionA
        ? chartDefinition.dimensionA.fieldType
        : undefined,
      chartDef: chartDefinition,
      fontSize: 11,
      formatMetric,
      xMetricId: xEntry.metric.id,
      yMetricId: yEntry.metric.id,
      sizeMetricId: sizeEntry ? sizeEntry.metric.id : undefined,
      xMetricName: xEntry.metric.name,
      yMetricName: yEntry.metric.name,
      sizeMetricName: sizeEntry ? sizeEntry.metric.name : undefined,
      dateScope,
    });

    setOptions({
      ...baseBubbleOptions,
      series: [
        {
          name: `${xEntry.metric.name} vs ${yEntry.metric.name}`,
          type: 'bubble',
          metricIds,
          onDrillDown,
          animation: false,
          data: points,
        },
      ],
    } as any as Highcharts.Options);
    setIsNoDataToDisplay(points.length === 0);
  }, [
    chartDefinition,
    data,
    dateScope,
    formatMetric,
    groupField,
    onDrillDown,
    openMenu,
    unitsLocale,
  ]);

  return {
    options,
    isNoData: isNoDataToDisplay,
    isReady: !!data && !isLoading,
  };
};

export default useBubbleData;
